/**
 * Bot decision core (Arena §Bot AI, Movement spec §6).
 *
 * ONE shared brain. `arena/sim.ts` (headless harness) and `views/arena-match.ts`
 * (the live match) are deliberately different MAPS, but they must not be different
 * OPPONENTS — so every behavioural rule lives here, and both callers are thin
 * adapters that feed it a perceived world (see arena/bot-sense.ts) and execute the
 * one intent it returns.
 *
 * The charter this implements, in the owner's words: bots play by the rules, use
 * only what a human can see, react with human latency, make mistakes, differ from
 * one another, and never stall or cheat.
 *
 * WHAT CHANGED, 2026-09-02
 * ------------------------
 * This used to be a flat nine-branch priority list with no memory: it re-picked a
 * target five times a second (so bots flipped mid-swing), never evaluated whether a
 * fight was winnable, never kited or repositioned, patrolled in a ring around the
 * sanctuary, and its geyser-dodge branch had never once fired because both callers
 * passed `armedHazard: null`. It is now a small state machine with per-bot memory:
 * target commitment, a trade evaluation, kiting, a disengage that goes AWAY from
 * the threat instead of always to the middle, a real geyser reflex, and a patrol
 * that roams the whole map.
 *
 * PURITY. `decide()` is still a pure function of its argument when no memory is
 * passed — the existing test suite depends on that. `world.memory`, when present,
 * is caller-owned scratch that `decide` is explicitly allowed to update (target
 * commitment, blunder windows, the shrink-notice clock). No global state.
 *
 * STABILITY. `decide()` never throws. One bot making a bad decision must not kill
 * the match loop for the other twenty-four.
 */

import type { BotProfile } from './profile';
import { combatPower, TIER_COMBAT } from './combat';
import { Perception } from '../nav/perception';

/**
 * A rival, as PERCEIVED. Note what is missing.
 *
 * `pendingGold` used to be here and was read by scoreTarget — no human can see how
 * much unbanked gold another fighter is carrying, so bots were target-picking off
 * hidden information. It is replaced by `kills`, which everybody watches go by in
 * the killfeed: a bounty you can actually justify. Exact `hp`/`maxHp` are gone the
 * same way, replaced by a banded, possibly stale `hpPct`.
 */
export interface EnemyView {
  id: string;
  x: number; y: number;
  /** Health as last SEEN, snapped to quarters. Not live, not exact. */
  hpPct: number;
  /** Public kill count (the killfeed). The observable stand-in for "fat target". */
  kills: number;
  /** Distance from the bot to the last known position. */
  dist: number;
  /** ms since actually seen. >0 means this position is remembered, not observed. */
  staleMs: number;
  /** In sight right now (as opposed to remembered). */
  visible: boolean;
}

export interface AiSelf {
  id: string;
  x: number; y: number;
  hp: number; maxHp: number;
  pendingGold: number;
  profile: BotProfile;
  /** Can't leave the safe space until this reaches 0 (ms). */
  safeCooldownMs: number;
  inSafe: boolean;
}

/**
 * Per-bot mutable scratch. The CALLER owns it and passes it in; `decide` only ever
 * touches the fields documented here. Holding it outside the decision function is
 * what keeps the function testable and the harness deterministic.
 */
export interface BotMemory {
  /** Perception memory: reaction latency + 4 s last-known positions. */
  perception: Perception;
  /** Last-seen HP band / kill count per enemy id (see bot-sense.ts). */
  facts: Map<string, { hpPct: number; kills: number }>;
  /** Where the bot is looking — drives the vision cone. */
  facing: number;
  /** Who it is fighting, and until when it refuses to be distracted. */
  targetId: string | null;
  committedUntilMs: number;
  /** When the bot NOTICES the map is closing (it is not told the instant it flips). */
  shrinkNoticedAtMs: number | null;
  /** Currently committed to a deliberately poor target choice (a mistake). */
  blunderUntilMs: number;
  /** Decision stagger: the caller may use this to spread AI ticks across frames. */
  aiCdMs: number;
  /** Last intent, replayed by the caller between staggered decision ticks. */
  last: Intent | null;
}

export function newBotMemory(reactionMs: number, aiCdMs = 0): BotMemory {
  return {
    perception: new Perception(reactionMs),
    facts: new Map(),
    facing: 0,
    targetId: null,
    committedUntilMs: 0,
    shrinkNoticedAtMs: null,
    blunderUntilMs: 0,
    aiCdMs,
    last: null,
  };
}

export interface AiWorld {
  self: AiSelf;
  enemies: EnemyView[];               // already filtered by perception (cone + LOS + reaction)
  safeCenter: { x: number; y: number };
  distToSafe: (x: number, y: number) => number;
  nearestChokepoint: (x: number, y: number) => { x: number; y: number } | null;
  /**
   * STATIC hazard cost at a point (lava, geyser fields) — map knowledge a player also
   * has. Enemy threat is NOT in here: that is derived from `enemies`, which is already
   * perception-gated, so a retreating bot can only route around rivals it has seen.
   */
  danger: (x: number, y: number) => number;
  /** An armed geyser within dodge distance, or null. */
  armedHazard: { x: number; y: number } | null;
  shrinking: boolean;
  /**
   * Does the closing ring actually DAMAGE anyone outside it?
   *
   * The headless sim has a lethal ring; the live match does not — its only damage
   * source is a swing. `shrinking` alone therefore meant "the last 60 seconds", and
   * running for the middle on it made every bot abandon combat for the whole finale
   * and pile into the sanctuary: a dead climax, and the exact crowding the sanctuary
   * heal was removed to stop. Retreating from a ring is only rational when the ring
   * can kill you.
   */
  ringLethal?: boolean;
  attackRange: number;
  aliveCount: number;
  /** Match clock in ms. */
  nowMs: number;
  /** The playable map, in tiles. See clampToMap. */
  bounds?: { w: number; h: number };
  /** Per-bot scratch. Omit it and `decide` is a pure function (tests rely on this). */
  memory?: BotMemory;
  /** Seeded randomness for latency jitter and mistakes. Omit → deterministic 0.5. */
  rng?: () => number;
}

/** Keep an AI destination inside the walkable interior of whatever map we are on. */
export function clampToMap(x: number, y: number, world: AiWorld): { x: number; y: number } {
  const b = world.bounds;
  if (!b) return { x, y };
  const m = MAP_MARGIN;
  return { x: Math.max(m, Math.min(b.w - m, x)), y: Math.max(m, Math.min(b.h - m, y)) };
}

/** Tiles of border the AI never aims into — the field's unwalkable band, plus slack. */
const MAP_MARGIN = 3;

export type Intent =
  | { kind: 'attack'; targetId: string }
  | { kind: 'dodge'; x: number; y: number }
  | { kind: 'move'; x: number; y: number; purpose: 'chase' | 'retreat' | 'reposition' | 'wander' | 'shrink' | 'bank' }
  | { kind: 'idle' };

const SURROUND_RADIUS = 6;
const SURROUND_COUNT = 3;

/**
 * Patrol, for a bot with nothing to fight.
 *
 * The destination has to be STABLE — `decide()` runs every tick and the mover steps
 * toward whatever it returns, so a freshly rolled point each call would make a bot
 * vibrate in place rather than travel. It is hashed from the bot's id and a coarse
 * time bucket: fixed for WANDER_PERIOD_MS, then somewhere new. That keeps `decide`
 * pure, which is what its test suite relies on.
 *
 * The RADIUS is why this rewrite matters. It used to top out at 44 tiles regardless
 * of map size, and every destination was expressed from the safe centre, so on the
 * 80x80 live field the whole roster patrolled a ring around the sanctuary — half of
 * the owner's "too much movement around the safe zone". The outer bound now scales
 * with the map (0.45 × its short side), so patrols reach the corners. The inner
 * bound stays: 16 tiles is far enough out that nobody loiters on the church steps.
 */
const WANDER_PERIOD_MS = 7000;
const WANDER_MIN_R = 16;
const WANDER_MAX_R = 44;

export function wanderTarget(world: AiWorld): { x: number; y: number } {
  const bucket = Math.floor(world.nowMs / WANDER_PERIOD_MS);
  let h = 2166136261;
  const id = world.self.id;
  for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 16777619); }
  h ^= bucket; h = Math.imul(h, 16777619);
  const u = (h >>> 0) / 4294967296;
  const v = ((Math.imul(h ^ 0x9e3779b9, 16777619)) >>> 0) / 4294967296;
  const ang = u * Math.PI * 2;
  const b = world.bounds;
  const maxR = b ? Math.max(WANDER_MIN_R + 4, Math.min(b.w, b.h) * 0.45) : WANDER_MAX_R;
  const r = WANDER_MIN_R + v * (maxR - WANDER_MIN_R);
  return clampToMap(world.safeCenter.x + Math.cos(ang) * r, world.safeCenter.y + Math.sin(ang) * r, world);
}

/**
 * Utility target selection (spec §6 row 0): nearest by distance, weighted so a bot
 * finishes the wounded, avoids crowds it can't win, and goes after a fighter with a
 * name in the killfeed. Higher score = better target.
 *
 * The gold term is gone (see EnemyView) and a STALENESS term is new: a rival you last
 * glimpsed three seconds ago is a worse bet than the one in front of you, because you
 * are guessing where they are.
 */
export function scoreTarget(self: AiSelf, e: EnemyView, world: AiWorld): number {
  const p = self.profile;
  const distScore = 1 / (1 + e.dist);                                   // closer is better
  const woundScore = (1 - e.hpPct) * p.targetHpWeight;                  // finishing pays
  const bountyScore = Math.min(1, e.kills / 4) * 0.5;                   // the killfeed's big name
  // Crowding around the target — enemies near it we'd also have to fight.
  let crowd = 0;
  for (const o of world.enemies) if (o.id !== e.id && Math.hypot(o.x - e.x, o.y - e.y) < 5) crowd++;
  const crowdPenalty = crowd * 0.15 * p.cautionWeight;
  const ownHpFactor = self.hp / self.maxHp;                             // hurt → less eager to commit
  const staleness = 1 - Math.min(0.5, e.staleMs / 8000);                // guessing costs
  return ((distScore + woundScore + bountyScore) * (0.5 + 0.5 * ownHpFactor) * p.aggression - crowdPenalty) * staleness;
}

/** Pick the best target, or null if none worth engaging. */
export function selectTarget(world: AiWorld): EnemyView | null {
  let best: EnemyView | null = null, bestScore = -Infinity;
  for (const e of world.enemies) {
    const s = scoreTarget(world.self, e, world);
    if (s > bestScore) { bestScore = s; best = e; }
  }
  return best;
}

/**
 * Target selection WITH commitment and mistakes.
 *
 * Without memory this is just `selectTarget`. With it:
 *  - the current target is kept until `committedUntilMs`, unless it is gone from
 *    perception entirely — bots used to re-rank five times a second and abandon a
 *    fight they were one swing from winning;
 *  - switching early needs the newcomer to beat the incumbent by a margin that
 *    scales with how committed the personality is;
 *  - `blunderChance` of the time it deliberately takes the SECOND best and stays on
 *    it for longer than usual. A bot that always picks correctly is not a person.
 */
function commitTarget(world: AiWorld, rnd: () => number): EnemyView | null {
  const mem = world.memory;
  const p = world.self.profile;
  if (!mem) return selectTarget(world);

  const byId = (id: string | null) => (id ? world.enemies.find((e) => e.id === id) ?? null : null);
  const current = byId(mem.targetId);
  if (current && world.nowMs < mem.committedUntilMs) return current;

  const ranked = [...world.enemies].sort((a, b) => scoreTarget(world.self, b, world) - scoreTarget(world.self, a, world));
  if (!ranked.length) { mem.targetId = null; return null; }

  const blundering = ranked.length > 1 && rnd() < p.blunderChance;
  let pick = blundering ? ranked[1] : ranked[0];
  if (current && pick.id !== current.id) {
    // Hysteresis: the challenger must be clearly better, not marginally.
    const margin = 1 + 0.35 * (p.commitmentMs / 3000);
    if (scoreTarget(world.self, pick, world) < scoreTarget(world.self, current, world) * margin) pick = current;
  }
  if (pick.id !== mem.targetId || world.nowMs >= mem.committedUntilMs) {
    mem.targetId = pick.id;
    const stretch = blundering ? 1.8 : 1;
    mem.committedUntilMs = world.nowMs + p.commitmentMs * stretch * (0.7 + 0.6 * rnd());
    if (blundering) mem.blunderUntilMs = mem.committedUntilMs;
  }
  return pick;
}

function enemiesWithin(world: AiWorld, x: number, y: number, r: number): number {
  let n = 0;
  for (const e of world.enemies) if (Math.hypot(e.x - x, e.y - y) <= r) n++;
  return n;
}

/**
 * Perceived threat at a point: the enemies THIS BOT has seen, weighted by closeness,
 * plus whatever static hazard the map reports there. Deliberately built from
 * `world.enemies` rather than from a global danger field — a shared danger map would
 * hand every bot the position of rivals it has never laid eyes on, which is the exact
 * cheat this pass exists to remove. It is also O(seen), so it costs nothing.
 */
function threatAt(world: AiWorld, x: number, y: number): number {
  let t = world.danger(x, y);
  for (const e of world.enemies) {
    const d = Math.hypot(e.x - x, e.y - y);
    if (d < 16) t += 1 / (1 + d);
  }
  return t;
}

/**
 * Where to break off to.
 *
 * The old rule was "run to the safe space", which is why everyone orbited it — and
 * with the sanctuary no longer healing (owner, 2026-09-02) that retreat now buys
 * nothing at all except the gold in your pocket. So: a bot carrying real gold still
 * runs for the bank, because banking is the only thing the sanctuary is for. A broke
 * bot instead disengages toward the quietest ground it can see, sampling a ring of
 * candidate directions and taking the calmest — weighted by its own cautionWeight, so
 * a Coward runs further and a Tactician only steps out of the crossfire.
 */
const DISENGAGE_DIRS = 8;
function disengage(world: AiWorld): Intent {
  const s = world.self;
  const reach = 8 + 4 * Math.min(2, s.profile.cautionWeight);
  let best: { x: number; y: number } | null = null, bestScore = Infinity;
  for (let i = 0; i < DISENGAGE_DIRS; i++) {
    const a = (i / DISENGAGE_DIRS) * Math.PI * 2;
    const c = clampToMap(s.x + Math.cos(a) * reach, s.y + Math.sin(a) * reach, world);
    const score = threatAt(world, c.x, c.y);
    if (score < bestScore) { bestScore = score; best = c; }
  }
  const to = best ?? { x: s.x, y: s.y };
  return { kind: 'move', x: to.x, y: to.y, purpose: 'retreat' };
}

/**
 * Am I winning this trade? Uses `combatPower` — which had sat in combat.ts unused
 * since it was written — against an estimate of the target built from MY OWN stats
 * and THEIR VISIBLE HP band. That is the honest estimate: a bot cannot read a
 * rival's tier or gear, so it assumes they hit about as hard as it does and judges
 * the fight on health, which is exactly what a player does.
 */
function winningTrade(self: AiSelf, e: EnemyView): number {
  const stats = TIER_COMBAT[self.profile.tier] ?? TIER_COMBAT.regular;
  const mine = combatPower(self.hp, stats);
  const theirs = combatPower(Math.max(1, e.hpPct * self.maxHp), stats);
  return mine / Math.max(1, theirs);
}

/** Step to the edge of reach, away from the target: buy cooldown without fleeing. */
function kite(world: AiWorld, e: EnemyView): Intent {
  const s = world.self;
  const dx = s.x - e.x, dy = s.y - e.y, l = Math.hypot(dx, dy) || 1;
  const want = world.attackRange * 2.2;
  const to = clampToMap(e.x + (dx / l) * want, e.y + (dy / l) * want, world);
  return { kind: 'move', x: to.x, y: to.y, purpose: 'reposition' };
}

/**
 * Does this bot KNOW the map is closing yet?
 *
 * `world.shrinking` is derived from the match clock, which is on the player's HUD, so
 * the information is fair — but it used to arrive as a boolean that flipped for all
 * twenty-five bots on the same frame, and the whole field broke for the centre in
 * lockstep. A human has to notice. Each bot gets its own noticing delay, scaled off
 * its reaction time and jittered.
 */
function shrinkNoticed(world: AiWorld, rnd: () => number): boolean {
  if (!world.shrinking) return false;
  // No teeth, no stampede — see AiWorld.ringLethal.
  if (world.ringLethal === false) return false;
  const mem = world.memory;
  if (!mem) return true;
  if (mem.shrinkNoticedAtMs === null) mem.shrinkNoticedAtMs = world.nowMs + world.self.profile.reactionMs * 2 + rnd() * 2500;
  return world.nowMs >= mem.shrinkNoticedAtMs;
}

let warnedOnce = false;

/**
 * The behaviour tree: return the single intent this bot should act on.
 *
 * Wrapped so it CANNOT throw. Both callers used to invoke this bare inside the match
 * loop, so a single bad decision — a missing target, an undefined chokepoint — would
 * have taken the whole match down with it. Charter: "recovers from errors."
 */
export function decide(world: AiWorld): Intent {
  try {
    return decideCore(world);
  } catch (err) {
    if (!warnedOnce) { warnedOnce = true; console.warn('[bot-ai] decision failed, idling this tick', err); }
    return { kind: 'idle' };
  }
}

function decideCore(world: AiWorld): Intent {
  const s = world.self;
  const p = s.profile;
  const rnd = world.rng ?? (() => 0.5);
  const hpPct = s.hp / s.maxHp;

  // 1. Reflex — an armed geyser under/near us: dodge perpendicular. Beats everything.
  if (world.armedHazard) {
    const dx = s.x - world.armedHazard.x, dy = s.y - world.armedHazard.y;
    const l = Math.hypot(dx, dy) || 1;
    const to = clampToMap(s.x + (dx / l) * 3.5, s.y + (dy / l) * 3.5, world);
    return { kind: 'dodge', x: to.x, y: to.y };
  }

  // 2. Locked in the safe space (prep or cooldown) → don't try to leave.
  if (s.inSafe && s.safeCooldownMs > 0) return { kind: 'idle' };

  // 3. Cooldown up and standing in the sanctuary → LEAVE. There is no gold in here,
  // combat does nothing in here, and since 2026-09-02 there is no healing either, so
  // a bot that lingers is simply not playing. Strictly OUTWARD, so the exit does not
  // turn into another lap of the ring the owner complained about.
  if (s.inSafe && s.safeCooldownMs <= 0) {
    let dx = s.x - world.safeCenter.x, dy = s.y - world.safeCenter.y;
    const l = Math.hypot(dx, dy);
    if (l < 0.5) { dx = Math.cos(s.x * 7.13); dy = Math.sin(s.y * 3.71); }   // dead-centre → any way out
    const ll = Math.hypot(dx, dy) || 1;
    const out = clampToMap(world.safeCenter.x + (dx / ll) * 22, world.safeCenter.y + (dy / ll) * 22, world);
    return { kind: 'move', x: out.x, y: out.y, purpose: 'reposition' };
  }

  // 5. Hurt, with something close enough to kill us → break off.
  //    Gold in the pocket goes to the bank (the sanctuary's whole remaining purpose);
  //    empty-handed, head for the quietest ground instead of adding to the scrum in
  //    the middle of the map.
  if (hpPct < p.fleeHpPct && enemiesWithin(world, s.x, s.y, world.attackRange + 4) > 0) {
    if (s.pendingGold >= 20 && !world.shrinking) {
      return { kind: 'move', x: world.safeCenter.x, y: world.safeCenter.y, purpose: 'retreat' };
    }
    return disengage(world);
  }

  const target = commitTarget(world, rnd);

  // 6. Something in striking range. Don't run from an adjacent fight — in a 25-bot
  // cluster where everyone is "surrounded", nobody would ever swing. But DO judge the
  // trade: a bot that is losing badly and has the caution to know it steps back out to
  // the edge of reach instead of standing still trading blows it cannot win.
  const inReach = world.enemies.filter((e) => e.dist <= world.attackRange && e.visible);
  if (inReach.length) {
    let best = inReach[0];
    for (const e of inReach) if (scoreTarget(s, e, world) > scoreTarget(s, best, world)) best = e;
    if (target && inReach.some((e) => e.id === target.id)) best = target;   // honour the commitment
    const edge = winningTrade(s, best);
    const bail = 0.75 * p.cautionWeight / Math.max(0.3, p.aggression);
    if (edge < bail && hpPct < 0.6 && inReach.length === 1) return kite(world, best);
    return { kind: 'attack', targetId: best.id };
  }

  // 6b. Map closing — but only once this bot has NOTICED (per-personality delay), and
  //     only below the strike branch above: nobody walks away from someone already in
  //     reach because a timer changed.
  if (!s.inSafe && shrinkNoticed(world, rnd)) {
    return { kind: 'move', x: world.safeCenter.x, y: world.safeCenter.y, purpose: 'shrink' };
  }

  // 7. Carrying real gold and reasonably safe → bank it (greedy bots hold longer).
  const bankUrge = s.pendingGold * (1 - p.greed) / 100;
  if (bankUrge > 1 && enemiesWithin(world, s.x, s.y, 5) === 0) {
    return { kind: 'move', x: world.safeCenter.x, y: world.safeCenter.y, purpose: 'bank' };
  }

  // 8. Surrounded (3+ within 6) → make for a chokepoint to fight them one at a time.
  if (enemiesWithin(world, s.x, s.y, SURROUND_RADIUS) >= SURROUND_COUNT) {
    const choke = world.nearestChokepoint(s.x, s.y);
    if (choke) return { kind: 'move', x: choke.x, y: choke.y, purpose: 'reposition' };
    return disengage(world);
  }

  // 9. Fight: close on the committed target. If it is only REMEMBERED, this walks to
  // where it was last seen and the search falls out for free — the bot arrives, sees
  // nothing, the memory expires, and it goes back to patrolling. That is the whole
  // answer to "bots go blind and stop fighting" without giving them x-ray vision.
  if (target) {
    if (target.dist <= world.attackRange && target.visible) return { kind: 'attack', targetId: target.id };
    return { kind: 'move', x: target.x, y: target.y, purpose: 'chase' };
  }

  // 10. Nothing to fight → patrol. Chokepoint-lovers hold a bottleneck; everyone else
  // roams the whole map. `idle` is never a fallback here: standing still is what filled
  // the middle of the map with statues.
  if (p.chokepointAffinity > 0.7) {
    const choke = world.nearestChokepoint(s.x, s.y);
    if (choke) return { kind: 'move', x: choke.x, y: choke.y, purpose: 'reposition' };
  }
  const w = wanderTarget(world);
  return { kind: 'move', x: w.x, y: w.y, purpose: 'wander' };
}
